import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import "./ColoredRangeSlider.css";

const ColoredRangeSlider = ({
  min,
  max,
  value,
  onValueChange = () => {},
  ranges,
  mode = "gradient",
  markers = [],
  threshold = null,
  colors = {
    low: "var(--MDsim-error-color)",
    high: "var(--MDsim-green-color)",
  },
  isInteractive = false,
}) => {
  const sliderRef = useRef(null);
  const [sliderWidth, setSliderWidth] = useState(300);

  useEffect(() => {
    if (sliderRef.current) {
      setSliderWidth(sliderRef.current.offsetWidth);
    }
  }, []);

  const handleChange = e => {
    onValueChange(Number(e.target.value));
  };

  const getGradientStyle = () => {
    const getPercentage = val => ((val - min) / (max - min)) * 100;
    const clamp = val => Math.max(min, Math.min(max, val));

    const normalizeIntervals = intervals =>
      (intervals || [])
        .filter(
          interval =>
            Array.isArray(interval) &&
            interval.length === 2 &&
            Number.isFinite(interval[0]) &&
            Number.isFinite(interval[1]) &&
            interval[0] !== interval[1]
        )
        .map(interval => {
          const start = clamp(Math.min(interval[0], interval[1]));
          const end = clamp(Math.max(interval[0], interval[1]));
          return [start, end];
        })
        .filter(interval => interval[0] < interval[1]);

    if (mode === "biphasic") {
      // Biphasic mode: 2 regions (red and green)
      const thresholdValue = threshold !== null ? threshold : (min + max) / 2;
      const thresholdPercent = getPercentage(thresholdValue);

      return `linear-gradient(
        to right,
        ${colors.low} 0%,
        ${colors.low} ${thresholdPercent}%,
        ${colors.high} ${thresholdPercent}%,
        ${colors.high} 100%
      )`;
    }

    // Gradient mode: support arbitrary interval layouts.
    if (!ranges) {
      return "linear-gradient(to right, #ccc 0%, #ccc 100%)";
    }

    const greenRanges = normalizeIntervals(ranges.green);
    const yellowRanges = normalizeIntervals(ranges.yellow);
    const redRanges = normalizeIntervals(ranges.red);

    if (
      greenRanges.length === 0 &&
      yellowRanges.length === 0 &&
      redRanges.length === 0
    ) {
      return "linear-gradient(to right, #ccc 0%, #ccc 100%)";
    }

    const inAnyInterval = (value, intervals) =>
      intervals.some(([start, end]) => value >= start && value <= end);

    const getColorAtValue = value => {
      if (inAnyInterval(value, greenRanges)) return "var(--MDsim-green-color)";
      if (inAnyInterval(value, yellowRanges))
        return "var(--MDsim-yellow-color)";
      if (inAnyInterval(value, redRanges)) return "var(--MDsim-error-color)";
      // Outside explicit intervals should remain pathological by default.
      return "var(--MDsim-error-color)";
    };

    const boundaries = Array.from(
      new Set([
        min,
        max,
        ...greenRanges.flat(),
        ...yellowRanges.flat(),
        ...redRanges.flat(),
      ])
    )
      .sort((a, b) => a - b)
      .filter(val => val >= min && val <= max);

    if (boundaries.length < 2) {
      return "linear-gradient(to right, #ccc 0%, #ccc 100%)";
    }

    const rawSegments = [];
    for (let i = 0; i < boundaries.length - 1; i++) {
      const start = boundaries[i];
      const end = boundaries[i + 1];
      const mid = (start + end) / 2;
      rawSegments.push({ start, end, color: getColorAtValue(mid) });
    }

    if (rawSegments.length === 0) {
      return "linear-gradient(to right, #ccc 0%, #ccc 100%)";
    }

    const mergedSegments = rawSegments.reduce((acc, segment) => {
      const last = acc[acc.length - 1];
      if (last && last.color === segment.color) {
        last.end = segment.end;
      } else {
        acc.push({ ...segment });
      }
      return acc;
    }, []);

    const mixColors = (fromColor, toColor, toPct) => {
      // CSS-native interpolation keeps transitions smooth without hard bands.
      return `color-mix(in srgb, ${toColor} ${toPct}%, ${fromColor})`;
    };

    let minGapPct = Number.POSITIVE_INFINITY;
    for (let i = 0; i < mergedSegments.length - 1; i++) {
      const gapPct = Math.abs(
        getPercentage(mergedSegments[i + 1].start) -
          getPercentage(mergedSegments[i].end)
      );
      if (gapPct < minGapPct) {
        minGapPct = gapPct;
      }
    }

    const transitionWidthPct = Number.isFinite(minGapPct)
      ? Math.max(1.6, Math.min(3.8, minGapPct * 0.4 || 2.4))
      : 2.8;
    const stops = [`${mergedSegments[0].color} 0%`];

    for (let i = 0; i < mergedSegments.length - 1; i++) {
      const current = mergedSegments[i];
      const next = mergedSegments[i + 1];
      const boundaryPct = getPercentage(current.end);
      const leftStop = Math.max(0, boundaryPct - transitionWidthPct);
      const q1Stop = Math.max(0, boundaryPct - transitionWidthPct * 0.5);
      const midStop = boundaryPct;
      const q3Stop = Math.min(100, boundaryPct + transitionWidthPct * 0.5);
      const rightStop = Math.min(100, boundaryPct + transitionWidthPct);

      stops.push(`${current.color} ${leftStop}%`);
      stops.push(`${mixColors(current.color, next.color, 25)} ${q1Stop}%`);
      stops.push(`${mixColors(current.color, next.color, 50)} ${midStop}%`);
      stops.push(`${mixColors(current.color, next.color, 75)} ${q3Stop}%`);
      stops.push(`${next.color} ${rightStop}%`);
    }

    stops.push(`${mergedSegments[mergedSegments.length - 1].color} 100%`);

    return `linear-gradient(to right, ${stops.join(", ")})`;
  };

  const getMarkerPosition = markerValue => {
    const percentage = ((markerValue - min) / (max - min)) * 100;
    const rootStyles = getComputedStyle(document.documentElement);
    const sliderHeight = parseInt(
      rootStyles.getPropertyValue("--Slider-height")
    );
    const sliderBubbleTop = parseInt(
      rootStyles.getPropertyValue("--Slider-bubble-top")
    );
    const thumbSize = sliderHeight - sliderBubbleTop * 2;
    const thumbRadius = (thumbSize / 2 / sliderWidth) * 100;
    const adjustedPosition =
      thumbRadius + (percentage * (100 - 2 * thumbRadius)) / 100;

    return Math.max(thumbRadius, Math.min(100 - thumbRadius, adjustedPosition));
  };

  return (
    <div className="colored-slider-container">
      {/* Markers container */}
      {markers && markers.length > 0 && (
        <div className="colored-slider-markers">
          {markers.map((marker, index) => (
            <div
              key={index}
              className="colored-slider-marker"
              style={{
                left: `${getMarkerPosition(marker.value)}%`,
                color: marker.color || "#333",
              }}
            >
              <span className="marker-label">{marker.label}</span>
              <div
                className="marker-arrow"
                style={{ borderTopColor: marker.color || "#333" }}
              />
            </div>
          ))}
        </div>
      )}

      <input
        ref={sliderRef}
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={handleChange}
        className="colored-slider"
        disabled={!isInteractive}
        style={{
          background: getGradientStyle(),
        }}
      />
    </div>
  );
};

ColoredRangeSlider.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
  onValueChange: PropTypes.func,
  mode: PropTypes.oneOf(["gradient", "biphasic"]),
  ranges: PropTypes.shape({
    yellow: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
    green: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
    red: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  }),
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string,
    })
  ),
  threshold: PropTypes.number,
  colors: PropTypes.shape({
    low: PropTypes.string,
    high: PropTypes.string,
  }),
  isInteractive: PropTypes.bool,
};

export default ColoredRangeSlider;
